import { useCallback } from 'react';
import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';

type ByoSettingOutput = components['schemas']['Application.Common.Models.ByoSettingOutput'];
export type ByoSettingInput = components['schemas']['Application.Common.Models.ByoSettingInput'];

type ByoSettingsResponse = ByoSettingOutput[];

const BYO_SETTINGS_ENDPOINT: keyof paths = '/ByoSettings';

export interface UseByoSettingsReturn {
    data: ByoSettingsResponse | null;
    isLoading: boolean;
    error: string;
    deleteSetting: (id: number) => Promise<void>;
    createSetting: (data: ByoSettingInput) => Promise<void>;
    updateSetting: (data: ByoSettingInput) => Promise<void>;
}

export default function useByoSettings(): UseByoSettingsReturn {
    const { 
        data = [], 
        isLoading: isFetching, 
        error, 
        doFetch: reFetch
    } = useAxios<ByoSettingsResponse>({
        method: 'get',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: false,
    });

    const { 
        isLoading: isDeleting, 
        doFetch: deleteRequest 
    } = useAxios({
        method: 'delete',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const { 
        isLoading: isCreating, 
        doFetch: createRequest 
    } = useAxios<ByoSettingOutput>({
        method: 'post',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const { 
        isLoading: isUpdating, 
        doFetch: updateRequest 
    } = useAxios<ByoSettingOutput>({
        method: 'put',
        url: BYO_SETTINGS_ENDPOINT,
        lazy: true,
    });

    const isLoading = isFetching || isDeleting || isCreating || isUpdating;

    const deleteSetting = useCallback(
        async (id: number) => {
            await deleteRequest({
                url: `${BYO_SETTINGS_ENDPOINT}/${id}`,
            });
            await reFetch();
        },
        [deleteRequest, reFetch]
    );

    const createSetting = useCallback(
        async (settingData: ByoSettingInput) => {
            await createRequest({ data: settingData });
            await reFetch();
        },
        [createRequest, reFetch]
    );

    const updateSetting = useCallback(
        async (settingData: ByoSettingInput) => {
            await updateRequest({ data: settingData });
            await reFetch();
        },
        [updateRequest, reFetch]
    );

    return {
        data,
        isLoading,
        error,
        deleteSetting,
        createSetting,
        updateSetting,
    };
}
